/**
 * `<plp-form>` — orchestrator формы лидгена.
 *
 * Жизненный цикл:
 * 1. На connectedCallback — рендерит honeypot, ловит submit/click, подписывается
 *    на edit-mode и `tracking-context.snapshot()`.
 * 2. На submit — валидирует все вложенные `<plp-field>`, блокирует UI, всегда
 *    шлёт POST на `endpoint` и показывает success только после подтверждения
 *    сервером, что заявка сохранена.
 *
 * Tracking (UTM/referrer/landing) добавляется автоматически через
 * `extra_fields.utm_*` (payload-контракт обработчика `/api/forms/submit`,
 * см. SPEC-wc-02 раздел "Открытые вопросы").
 */

import { onEditModeChange, isEditMode } from '../core/edit-mode.js';
import { buildHoneypot, HONEYPOT_NAME } from './anti-spam.js';
import { mapFieldsToCrm, type FieldEntry } from './crm-mapper.js';
import { read as readTracking, snapshot as snapshotTracking } from './tracking-context.js';
import type { CanonicalFormSubmitPayload, FormKind } from '../../contracts/form-submit.js';
import type { PlpField } from './plp-field.js';
import { t } from '../i18n/index.js';
import {
  loadPageCommerceData,
  openCheckout,
  promptPendingOrderForOffer,
  readCommerceManifest,
  rememberCommerceOrder,
} from '../commerce/runtime.js';

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

interface SuccessAction {
  kind: 'dialog' | 'redirect' | 'popup' | 'inline';
  value: string;
}

interface FormSubmitResponse {
  result?: number;
  stored?: boolean;
  disposition?: 'accepted' | 'quarantined';
  error?: string;
  msg?: string;
  order?: {
    hash: string;
    path: string;
    amount: string;
    currency: string;
  };
}

class OrderFallbackSavedError extends Error {}
class OrderSubmitCancelledError extends Error {}

const DEFAULT_ENDPOINT = '/api/forms/submit';
let successDialogSequence = 0;

/**
 * SVG-спиннер для submit-кнопки. inline-flex чтобы не зависеть от
 * размера кнопки и наследовать её цвет через `currentColor`. Анимация
 * `spin` объявлена в `index.ts` (injectBaseStyles).
 */
function spinnerHtml(): string {
  return (
    '<span class="plp-form-spinner" style="display:inline-flex;align-items:center;gap:8px">' +
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" ' +
    'stroke="currentColor" stroke-width="3" stroke-linecap="round" ' +
    'aria-hidden="true" style="animation:plp-spin .9s linear infinite">' +
    '<path d="M21 12a9 9 0 1 1-6.22-8.56"/></svg>' +
    `<span>${t('form.sending')}</span></span>`
  );
}

export class PlpForm extends HTMLElement {
  private _state: SubmitState = 'idle';
  private _editUnsubscribe?: () => void;
  /** Anti-spam nonce от `/api/forms/nonce`, подтягивается в фоне. */
  private _nonce?: string;
  /** Стабильный id попытки: повтор после потери ответа не создаст второй Order. */
  private _orderSubmissionId?: string;

  connectedCallback(): void {
    snapshotTracking();
    this.appendHoneypot();
    // Первичный fetchNonce делает подписка ниже: onEditModeChange сразу
    // дёргает колбэк с текущим режимом, и вне редактора nonce уедет оттуда.
    this.addEventListener('submit', this.onSubmit);
    // Кнопки могут быть без оборачивающего <form>, поэтому ловим клики
    // на <button type="submit"> внутри корня.
    this.addEventListener('click', this.onMaybeSubmitClick);
    this._editUnsubscribe = onEditModeChange((edit) => {
      // Пересчёт состояния в момент входа в edit — например снять disabled
      // с кнопок если форма была в submitting (защита от зависшего состояния).
      // При выходе из edit-режима догоняем nonce, который не тянули в редакторе.
      if (!edit && this._nonce === undefined) this.fetchNonce();
    });
  }

  disconnectedCallback(): void {
    this._editUnsubscribe?.();
    this.removeEventListener('submit', this.onSubmit);
    this.removeEventListener('click', this.onMaybeSubmitClick);
  }

  private appendHoneypot(): void {
    if (this.querySelector(`input[name="${HONEYPOT_NAME}"]`)) return;
    this.appendChild(buildHoneypot());
  }

  /**
   * Фоново тянет anti-spam nonce (`GET /api/forms/nonce`) и кладёт в `this._nonce`.
   * `pid` передаём ТОЛЬКО как оптимизацию, если страница его отдаёт
   * (meta/`__plpPageId`); если нет — сервер выводит pageId из `Referer`, поэтому
   * запрос делаем в любом случае (иначе RawHtml/AI-страницы без client-side
   * pageId остались бы без nonce). Не блокирует рендер/сабмит: любая ошибка,
   * не-200 (например 204 — сервер отказал) или отсутствие nonce — тихо
   * игнорируются, форма остаётся отправляемой (fail-open).
   *
   * В edit-режиме (preview-iframe редактора) не ходим вовсе: сабмита там нет,
   * а `/api/forms/nonce` на хосте редактора отдаёт 404 и мусорит в консоль.
   */
  private fetchNonce(): void {
    if (isEditMode()) return;
    const pageId = readPageId();
    const url =
      pageId === null
        ? '/api/forms/nonce'
        : `/api/forms/nonce?pid=${encodeURIComponent(String(pageId))}`;
    void fetch(url, {
      method: 'GET',
      credentials: 'include',
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { nonce?: string } | null) => {
        if (data && typeof data.nonce === 'string') this._nonce = data.nonce;
      })
      .catch(() => {
        /* fail-open: без nonce форма всё равно отправляется */
      });
  }

  private fields(): PlpField[] {
    return Array.from(this.querySelectorAll<PlpField>('plp-field')).filter(
      // Фильтруем поля внутри вложенных <plp-form> (если такие будут).
      (el) => el.closest('plp-form') === this
    );
  }

  private submitButtons(): HTMLButtonElement[] {
    return Array.from(this.querySelectorAll<HTMLButtonElement>('button[type="submit"]')).filter(
      (el) => el.closest('plp-form') === this
    );
  }

  private setState(state: SubmitState): void {
    this._state = state;
    this.setAttribute('data-state', state);
  }

  private parseSuccess(): SuccessAction {
    const raw = this.getAttribute('success') ?? `dialog:${t('form.defaultSuccess')}`;
    const idx = raw.indexOf(':');
    if (idx === -1) return { kind: 'inline', value: raw };
    const kind = raw.slice(0, idx) as SuccessAction['kind'];
    const value = raw.slice(idx + 1);
    if (kind === 'dialog' || kind === 'redirect' || kind === 'popup' || kind === 'inline') {
      return { kind, value };
    }
    return { kind: 'inline', value: raw };
  }

  private onMaybeSubmitClick = (e: MouseEvent): void => {
    const target = e.target as HTMLElement | null;
    const button = target?.closest('button');
    if (!button) return;
    if (button.type !== 'submit') return;
    // Если кнопка внутри nested <form> — пускай родная форма обрабатывает
    if (button.closest('form') !== null) return;
    e.preventDefault();
    this.runSubmit();
  };

  private onSubmit = (e: Event): void => {
    e.preventDefault();
    this.runSubmit();
  };

  private async runSubmit(): Promise<void> {
    if (this._state === 'submitting') return;

    // Edit-mode: показываем тост, не отправляем.
    if (isEditMode()) {
      this.dispatchEvent(
        new CustomEvent('plp:form-submit-blocked', {
          bubbles: true,
          detail: { reason: 'edit-mode' },
        })
      );
      this.flashMessage(t('form.editModeDisabled'));
      return;
    }

    // Валидация всех полей. Невалидные → подсветка + фокус на первое.
    const fields = this.fields();
    let firstInvalid: PlpField | null = null;
    for (const field of fields) {
      const r = field.validate();
      if (!r.valid) {
        field.showError(r.error);
        firstInvalid ??= field;
      } else {
        field.showError(undefined);
      }
    }
    if (firstInvalid) {
      const focusable = firstInvalid.querySelector<HTMLElement>('input, select, textarea, button');
      focusable?.focus();
      return;
    }

    this.setState('submitting');
    this.lockUi(true);
    this.dispatchEvent(new CustomEvent('plp:form-submit-start', { bubbles: true }));

    try {
      const payload = this.buildPayload(fields);
      const body =
        this.formKind() === 'order'
          ? await this.submitOrder(payload)
          : await this.submitLead(payload);
      this.dispatchEvent(
        new CustomEvent('plp:form-submit-success', {
          bubbles: true,
          detail: {
            disposition: body.order ? 'order' : (body.disposition ?? 'accepted'),
            order: body.order,
          },
        })
      );
      if (body.order) {
        this.setState('success');
        rememberCommerceOrder(
          body.order,
          'offer',
          readCommerceManifest()?.page_id,
          this.selectedOfferPublicId()
        );
        openCheckout(body.order.path, this.getAttribute('data-plp-presentation') ?? 'modal');
        this.resetAfterSuccess();
        this._orderSubmissionId = undefined;
        return;
      }
      this.handleSuccess(this.parseSuccess());
    } catch (err) {
      if (err instanceof OrderSubmitCancelledError) {
        this.setState('idle');
        this.lockUi(false);
        return;
      }
      this.setState('error');
      this.lockUi(false);
      // Nonce мог быть уже погашен серверным gate до сбоя хранилища. Получаем
      // новый заранее, чтобы повтор реального пользователя не стал replay.
      this._nonce = undefined;
      this.fetchNonce();
      this.dispatchEvent(
        new CustomEvent('plp:form-submit-error', {
          bubbles: true,
          detail: { error: err instanceof Error ? err.message : String(err) },
        })
      );
      this.flashMessage(
        err instanceof OrderFallbackSavedError
          ? t('form.defaultSuccess')
          : (this.getAttribute('error') ?? t('form.defaultError'))
      );
    }
  }

  private async submitLead(payload: CanonicalFormSubmitPayload): Promise<FormSubmitResponse> {
    const res = await fetch(this.endpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'include',
    });
    const body = (await res.json().catch(() => null)) as FormSubmitResponse | null;
    if (!res.ok || body?.result !== 1 || body.stored !== true) {
      throw new Error(body?.error || body?.msg || `HTTP ${res.status}`);
    }
    return body;
  }

  private async submitOrder(payload: CanonicalFormSubmitPayload): Promise<FormSubmitResponse> {
    this._orderSubmissionId ??= createSubmissionId();
    const offerPublicId = this.selectedOfferPublicId();
    const pending = await promptPendingOrderForOffer(offerPublicId);
    if (pending.decision === 'cancel') throw new OrderSubmitCancelledError();
    if (pending.decision === 'continue' && pending.order) {
      return { order: pending.order };
    }
    try {
      const manifest = readCommerceManifest();
      if (!manifest) throw new Error('commerce manifest is missing');
      const data = await loadPageCommerceData();
      const projection = data.offers[offerPublicId];
      if (!projection?.available) throw new Error('offer is unavailable');

      const response = await fetch('/api/checkout/orders/offer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          submission_id: this._orderSubmissionId,
          page_id: manifest.page_id,
          offer_public_id: offerPublicId,
          offer_version: projection.version,
          fields: {
            name: this.scalarField(payload.fields.name),
            email: this.scalarField(payload.fields.email),
            phone: this.scalarField(payload.fields.phone),
          },
          extra_fields: payload.extra_fields,
          manifest,
        }),
      });
      const body = (await response.json().catch(() => null)) as FormSubmitResponse | null;
      if (!response.ok || !body?.order?.path) {
        throw new Error(body?.error || body?.msg || `HTTP ${response.status}`);
      }
      return body;
    } catch (error) {
      await this.saveOrderFallback(payload, error);
      throw error;
    }
  }

  private selectedOfferPublicId(): string {
    const fixed = this.getAttribute('data-plp-offer-public-id')?.trim();
    const allowed = (this.getAttribute('data-plp-offer-public-ids') ?? '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);
    if (fixed) allowed.push(fixed);
    const unique = new Set(allowed);
    if (unique.size === 0) throw new Error('order form has no offers');
    if (fixed) return fixed;

    const selector = this.fields().find(
      (field) =>
        field.getAttribute('role') === 'offer' || field.getAttribute('name') === 'offer_public_id'
    );
    const selected = selector ? this.scalarField(selector.getValue()) : undefined;
    if (!selected || !unique.has(selected)) throw new Error('order form offer is invalid');
    return selected;
  }

  private scalarField(value: string | string[] | undefined): string | undefined {
    const scalar = Array.isArray(value) ? value[0] : value;
    return typeof scalar === 'string' && scalar.trim() ? scalar.trim() : undefined;
  }

  private async saveOrderFallback(
    payload: CanonicalFormSubmitPayload,
    error: unknown
  ): Promise<void> {
    const diagnostic = error instanceof Error ? error.message : String(error);
    const fallback: CanonicalFormSubmitPayload = {
      ...payload,
      type: 'lead',
      extra_fields: {
        ...payload.extra_fields,
        commerce_fallback: 'order_not_created',
        commerce_submission_id: this._orderSubmissionId ?? '',
        commerce_error: diagnostic.slice(0, 500),
      },
    };
    try {
      await this.submitLead(fallback);
      this.dispatchEvent(
        new CustomEvent('plp:order-fallback-saved', {
          bubbles: true,
          detail: { error: diagnostic },
        })
      );
      throw new OrderFallbackSavedError(diagnostic);
    } catch (fallbackError) {
      if (fallbackError instanceof OrderFallbackSavedError) throw fallbackError;
      throw error;
    }
  }

  private endpoint(): string {
    return this.getAttribute('endpoint') ?? DEFAULT_ENDPOINT;
  }

  private buildPayload(fields: PlpField[]): CanonicalFormSubmitPayload {
    const entries: FieldEntry[] = [];
    for (const f of fields) {
      if (!f.shouldSubmit()) continue;
      const name = f.getAttribute('name');
      if (!name) continue;
      entries.push({
        name,
        value: f.getValue(),
        crm: f.getAttribute('crm') ?? undefined,
      });
    }
    const { fields: standard, extra_fields } = mapFieldsToCrm(entries);

    // Tracking-блок добавляем в extra_fields для совместимости с
    // payload-контрактом /api/forms/submit (см. SPEC-wc-02, открытый вопрос tracking).
    const tracking = readTracking();
    for (const [key, value] of Object.entries(tracking)) {
      if (value === undefined || key === 'captured_at') continue;
      if (!(key in extra_fields)) extra_fields[key] = value;
    }

    const pageId = readPageId();

    const hpInput = this.querySelector<HTMLInputElement>(`input[name="${HONEYPOT_NAME}"]`);

    const payload: CanonicalFormSubmitPayload = {
      type: this.formKind(),
      fields: standard,
      extra_fields,
      // Anti-spam gate (см. docs/form-antispam-gate.md, Шаг 2):
      // nonce — доказательство рендера страницы (может быть пустым, если
      // фоновый fetch ещё не вернулся / отказал — сервер fail-open),
      // hp — значение honeypot-инпута (у реального юзера пустая строка).
      nonce: this._nonce ?? '',
      hp: hpInput?.value ?? '',
    };
    const formName = this.getAttribute('name')?.trim();
    if (formName) payload.form_name = formName;
    if (pageId !== null) payload.page_id = pageId;
    return payload;
  }

  private formKind(): FormKind {
    const value = this.getAttribute('type');
    return value === 'subscribe' || value === 'order' ? value : 'lead';
  }

  private lockUi(locked: boolean): void {
    for (const button of this.submitButtons()) {
      button.disabled = locked;
      this.applySpinner(button, locked);
    }
    for (const field of this.fields()) {
      field.setDisabled(locked);
    }
  }

  /**
   * Подменить содержимое submit-кнопки на спиннер + сохранённый текст
   * на время отправки. При снятии — восстановить оригинальный innerHTML.
   * Сохраняем именно innerHTML (а не textContent), чтобы юзерский HTML
   * с иконками внутри кнопки не терялся.
   */
  private applySpinner(button: HTMLButtonElement, on: boolean): void {
    if (on) {
      if (button.dataset.plpOriginalHtml === undefined) {
        button.dataset.plpOriginalHtml = button.innerHTML;
      }
      button.setAttribute('aria-busy', 'true');
      button.innerHTML = spinnerHtml();
    } else {
      const saved = button.dataset.plpOriginalHtml;
      if (saved !== undefined) {
        button.innerHTML = saved;
        delete button.dataset.plpOriginalHtml;
      }
      button.removeAttribute('aria-busy');
    }
  }

  private handleSuccess(action: SuccessAction): void {
    this.setState('success');
    if (action.kind === 'redirect') {
      try {
        location.href = action.value;
      } catch {
        /* ignore */
      }
      return;
    }
    if (action.kind === 'popup') {
      this.dispatchEvent(
        new CustomEvent('plp:popup-open', {
          bubbles: true,
          detail: { name: action.value },
        })
      );
      this.resetAfterSuccess();
      return;
    }
    if (action.kind === 'inline') {
      this.replaceWithSuccessMessage(action.value);
      return;
    }

    this.showSuccessDialog(action.value);
  }

  private resetAfterSuccess(): void {
    for (const f of this.fields()) {
      const inputs = f.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
        'input, textarea, select'
      );
      inputs.forEach((el) => {
        if (el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio')) {
          el.checked = false;
        } else {
          el.value = '';
        }
      });
    }
    this.lockUi(false);
    this._nonce = undefined;
    this.fetchNonce();
    this.setState('idle');
  }

  private showSuccessDialog(text: string): void {
    this.closeContainingModal();

    const dialog = document.createElement('dialog');
    const titleId = `plp-form-success-title-${++successDialogSequence}`;
    dialog.className = 'plp-form-success-dialog';
    dialog.setAttribute('aria-labelledby', titleId);

    const panel = document.createElement('div');
    panel.className = 'plp-form-success-dialog__panel';

    const title = document.createElement('h2');
    title.id = titleId;
    title.className = 'plp-form-success-dialog__title';
    title.textContent = t('form.successTitle');

    const message = document.createElement('p');
    message.className = 'plp-form-success-dialog__message';
    message.textContent = text;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'plp-form-success-dialog__button';
    button.textContent = t('form.successButton');

    panel.append(title, message, button);
    dialog.appendChild(panel);
    document.body.appendChild(dialog);

    let finished = false;
    const finish = (): void => {
      if (finished) return;
      finished = true;
      this.resetAfterSuccess();
      dialog.remove();
    };

    button.addEventListener('click', () => {
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      finish();
    });
    dialog.addEventListener('close', finish, { once: true });

    if (typeof dialog.showModal === 'function') {
      try {
        dialog.showModal();
        return;
      } catch {
        // Старый WebView: ниже используется доступный non-modal fallback.
      }
    }
    dialog.setAttribute('open', '');
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    button.focus();
  }

  private closeContainingModal(): void {
    const popover = this.closest<HTMLElement>('[popover]');
    if (popover && typeof popover.hidePopover === 'function') {
      try {
        popover.hidePopover();
      } catch {
        // Уже закрыт — можно продолжать success-flow.
      }
    }

    const dialog = this.closest<HTMLDialogElement>('dialog');
    if (dialog?.open && typeof dialog.close === 'function') {
      dialog.close();
    }
  }

  private replaceWithSuccessMessage(text: string): void {
    const div = document.createElement('div');
    div.className = 'plp-form-success rounded-lg bg-emerald-50 text-emerald-900 px-4 py-3';
    div.textContent = text;
    this.replaceWith(div);
  }

  private flashMessage(text: string): void {
    let toast = this.querySelector<HTMLElement>(':scope > .plp-form-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'plp-form-toast mt-3 rounded-lg bg-rose-50 text-rose-900 px-4 py-2 text-sm';
      this.appendChild(toast);
    }
    toast.textContent = text;
    window.setTimeout(() => toast?.remove(), 4000);
  }
}

function readPageId(): number | null {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="plp-page-id"]');
  if (meta) {
    const n = parseInt(meta.content, 10);
    return Number.isFinite(n) ? n : null;
  }
  const w = window as unknown as { __plpPageId?: number | string };
  if (w.__plpPageId !== undefined) {
    const n = typeof w.__plpPageId === 'number' ? w.__plpPageId : parseInt(w.__plpPageId, 10);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function createSubmissionId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
    16,
    20
  )}-${hex.slice(20)}`;
}
